import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { runInBackgroundCallback } from '../../../configuration/setup/backgroundActions';
import { usePutServiceProviderClientStatusMutation } from '../api/serviceProviderApi';
import { handleQueryError } from '../notifications/ErrorNotification';
import { showSuccessNotification } from '../notifications/SuccessNotification';
import { ConfirmButtonText } from '../sharedComponents/ConfirmButtonText';
import { ServiceProvider } from './serviceProvider.types';

type UpdateClientStatusConfirmationProps = {
    serviceProvider: ServiceProvider;
    show: boolean;
    setShow: (value: boolean) => void;
};

export const UpdateClientStatusConfirmation: FunctionComponent<UpdateClientStatusConfirmationProps> = ({
    serviceProvider,
    show,
    setShow,
}) => {
    const [updateClientStatus, updateClientStatusRequest] = usePutServiceProviderClientStatusMutation();
    const { accountId, interfaceClient, serviceProviderName } = serviceProvider;

    // This component is always rendered, but only makes sense if a client exist.
    // Thus, we do not render anything otherwise - which gives us better typing.
    if (!interfaceClient) {
        return null;
    }

    const enableClient = !interfaceClient.enabled;
    const messageIds = enableClient
        ? ({
              title: 'outboundOrderBook.serviceProviders.activateClient.title',
              text: 'outboundOrderBook.serviceProviders.activateClient.text',
              button: 'outboundOrderBook.serviceProviders.button.activateClient',
              success: 'outboundOrderBook.serviceProviders.activateClient.success',
          } as const)
        : ({
              title: 'outboundOrderBook.serviceProviders.deactivateClient.title',
              text: 'outboundOrderBook.serviceProviders.deactivateClient.text',
              button: 'outboundOrderBook.serviceProviders.button.deactivateClient',
              success: 'outboundOrderBook.serviceProviders.deactivateClient.success',
          } as const);

    const handleOnClick = runInBackgroundCallback(async () => {
        setShow(false);
        await updateClientStatus({
            accountId,
            clientId: interfaceClient.clientId,
            enabled: enableClient,
        })
            .unwrap()
            .then(() => showSuccessNotification(messageIds.success))
            .catch(handleQueryError);
    });

    return (
        <ConfirmationDialog
            show={show}
            title={<FormattedMessage id={messageIds.title} />}
            content={
                <FormattedMessage
                    id={messageIds.text}
                    values={{ serviceProviderName: <strong>{serviceProviderName}</strong> }}
                />
            }
            bsSize={Dialog.SIZE_SM}
            onClickConfirm={handleOnClick}
            onClickCancel={() => setShow(false)}
            cancelButtonText={<FormattedMessage id="outboundOrderBook.common.button.cancel" />}
            confirmButtonText={
                <ConfirmButtonText showSpinner={updateClientStatusRequest.isLoading} labelId={messageIds.button} />
            }
            useOverflow
        />
    );
};
